/* EqDialog class
 * Accessible modal/dialog helper built on the native HTML <dialog> element.
 *
 * Supports four content types out of the box: inline (content already in the DOM), image,
 * video (Video.js), and iframe (e.g. PDF preview). See MDN for how the native element works:
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dialog
 */

const eq_dialog_defaults = {
    selector: '.eq-dialog',
    show_close_button: true,
    close_button_text: 'Close dialog',
    aria_label: 'Dialog',
    // remove_inline_content: takes inline divs used for content and removes duplicates
    remove_inline_content: false,
    style: {
        width: '800px',
    },
    onOpen: null,
    description: false
}
// Default HTML template for dialog content
eq_dialog_defaults.template_inner = `<div class="eq-dialog-inner"></div>`;
eq_dialog_defaults.template_close_button = `<form class="eq-close-btn" method="dialog"><button autofocus aria-label="{{close_btn_txt}}"></button></form>`;

/**
 * Returns a safe absolute http(s) href for external links, or '' if the URL is invalid or disallowed.
 * Blocks javascript:, data:, etc.
 */
function eqDialogSafeExternalHref(raw) {
    if (!raw || typeof raw !== 'string') {
        return '';
    }
    const trimmed = raw.trim();
    if (!trimmed) {
        return '';
    }
    try {
        const resolved = new URL(trimmed, document.baseURI);
        const p = resolved.protocol.toLowerCase();
        if (p !== 'http:' && p !== 'https:') {
            return '';
        }
        return resolved.href;
    } catch (e) {
        return '';
    }
}

function eqDialogEscapeHtmlAttr(s) {
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

/**
 * Builds a "media document" link (Video Transcript / Disclosure Statement) for video dialogs.
 * Returns '' when the URL is missing or not a safe external http(s) URL.
 */
function eqDialogMediaDocLink(rawHref, linkLabel, ariaContext) {
    let safeHref = eqDialogSafeExternalHref(rawHref);
    if (!safeHref) {
        return '';
    }
    let hrefAttr = eqDialogEscapeHtmlAttr(safeHref);
    let aria = eqDialogEscapeHtmlAttr((ariaContext ? ariaContext + ' ' : '') + linkLabel + ' PDF (opens in a new window)');
    return `<a class="result-link link media-document-btn" href="${hrefAttr}" target="_blank" rel="noopener noreferrer" aria-label="${aria}"><span class="eqicon-PDF" aria-hidden="true"></span>${eqDialogEscapeHtmlAttr(linkLabel)}</a>`;
}

class EqDialog {
    constructor(options = {}) {
        this.settings = JSON.parse(JSON.stringify(eq_dialog_defaults));
        // override defaults if provided
        for (const prop in options) {
            this.settings[prop] = options[prop];
            if (prop === 'style') {
                for (const style_name in options.style) {
                    this.settings.style[style_name] = options.style[style_name];
                }
            }
        }
        
        // Listen for tab press, if modal is open keep focus locked
        document.addEventListener('keydown', (e) => {
            let dialog = document.querySelector('dialog[open]');
            if (!dialog) {
                return;
            }
            // Dynamically find all focusable elements and determine the first and last one
            let dialog_focusables = dialog.querySelectorAll('button,input,select,textarea,iframe,area,a,*[tabindex="0"]');
            if (!dialog_focusables.length) {
                return;
            }
            let first_focus = dialog_focusables.item(0);
            let last_focus = dialog_focusables.item(dialog_focusables.length - 1);
            if (!e.shiftKey && e.key === 'Tab') {
                if (document.activeElement == last_focus && first_focus) {
                    e.preventDefault();
                    first_focus.focus();
                }
            }
            if (e.shiftKey && e.key === 'Tab') {
                if (document.activeElement == first_focus && last_focus) {
                    e.preventDefault();
                    last_focus.focus();
                }
            }
            if (first_focus == last_focus) {
                let close_button = dialog.querySelector("button");
                if (close_button) {
                    close_button.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            }
        });
    }

    // Find all dialog buttons/links and generate HTML
    init() {
        let dialog_btns = document.querySelectorAll(this.settings.selector);
        dialog_btns.forEach((el) => {
            let dialog_type, dialog_id, href, src, textDescription;
            dialog_type = el.dataset.type ? el.dataset.type : false;
            // Determine source of dialog content from href attribute or data-target attribute
            href = el.getAttribute('href');
            src = href ? href : el.dataset.target ? el.dataset.target : false;
            textDescription = el.dataset.description ? el.dataset.description : false;
            
            if (!src) {
                console.error(`Could not determine the dialog source. Please set the attribute 'href' or 'data-target'`);
                return;
            }
            // Determine what kind of dialog is being created
            if (!dialog_type) {
                if (src.match(/(^data:image\/[a-z0-9+\/=]*,)|(\.(jp(e|g|eg)|gif|png|bmp|webp|svg|ico)((\?|#).*)?$)/i)) {
                    dialog_type = 'image';
                } else if (src.match(/\.(mp4|mov|ogg|webm)((\?|#).*)?$/i)) {
                    dialog_type = 'video';
                } else if (src.match(/\.(pdf)((\?|#).*)?$/i)) {
                    dialog_type = 'iframe';
                } else if (src[0] === '#') {
                    dialog_type = 'inline';
                } else {
                    console.error(`Could not determine the dialog type for ${src}. Please specify the attribute 'data-type' or 'href'.`);
                    return;
                }
            }
            // Create an ID to assign to the dialog, take the existing ID attribute if there is one
            dialog_id = dialog_type === 'inline' && src ? src.substring(1) : this.generate_dialog_id(dialog_type);
            
            // Converts URL links to anchor link to prevent navigating away from page
            if (href) {
                el.setAttribute('href', `#${dialog_id}`);
                el.setAttribute('role', 'button');
            }
            // Assign event listeners the link/button to open the modal
            el.addEventListener('click', (e) => {
                e.preventDefault();
                let dlg = this.create_dialog({
                    dialog_type: dialog_type,
                    dialog_id: dialog_id,
                    src: src,
                    img_alt: el.dataset.imgAlt ? el.dataset.imgAlt : '',
                    close_button_text: el.dataset.closebtntxt ? el.dataset.closebtntxt : this.settings.close_button_text,
                    aria_label: el.dataset.arialabel ? el.dataset.arialabel : this.settings.aria_label,
                    description: textDescription,
                    dialog_title: el.dataset.dialogTitle || '',
                    transcript_url: el.dataset.transcriptUrl || '',
                    disclosure_url: el.dataset.disclosureUrl || '',
                    video_title: el.dataset.videoTitle || ''
                });
                if (!dlg) {
                    return;
                }
                dlg.showModal();
                
                if (typeof this.settings.onOpen === 'function') {
                    this.settings.onOpen(dlg);
                }
            });
        });
    }

    // Create and open a dialog immediately (programmatic API).
    // The source is either the ID of a target element (without the #) or the URL for the media file.
    // The optional `options` object forwards extra params (e.g. video_title, transcript_url,
    // disclosure_url, aria_label, close_button_text) so programmatic dialogs match data-attribute ones.
    open(dialog_type, source, textDescription, img_alt = '', options = {}) {
        if (dialog_type === 'inline' && !source) {
            console.error(`Inline dialogs must have a target.`);
            return;
        }
        // Note the parentheses: without them, '&& !source' would only apply to 'video'.
        if ((dialog_type === 'image' || dialog_type === 'iframe' || dialog_type === 'video') && !source) {
            console.error(`Media dialogs must have a source.`);
            return;
        }
        let dlg = this.create_dialog({
            dialog_type: dialog_type,
            dialog_id: dialog_type === 'inline' ? source : this.generate_dialog_id(dialog_type),
            src: dialog_type === 'inline' ? '' : source,
            img_alt: img_alt,
            close_button_text: options.close_button_text || this.settings.close_button_text,
            aria_label: options.aria_label || this.settings.aria_label,
            description: textDescription,
            dialog_title: options.dialog_title || '',
            transcript_url: options.transcript_url || '',
            disclosure_url: options.disclosure_url || '',
            video_title: options.video_title || ''
        });
        if (!dlg) {
            return;
        }
        dlg.showModal();
    }

    /* Creates HTML dialog element with given parameters
     * Parameters:
     * dialog_id - ID of the target element the inline dialog will pull content from
     * src - URL of the image or iframe to be displayed
     * img_alt - Alt text to add to the image dialog
     */
    create_dialog(params = {}) {
        let settings = this.settings;
        let has_form = false;
        // Create element for dialog
        let dialog = document.createElement('dialog');
        jq(dialog).attr('aria-modal', 'true');
        jq(dialog).attr('aria-label', params.aria_label);
        dialog.classList.add('eq-dialog-wrapper');
        dialog.innerHTML = settings.template_inner;

        let dialog_content = '';
        if (settings.show_close_button) {
            let close_btn_html = settings.template_close_button;
            close_btn_html = close_btn_html.replace('{{close_btn_txt}}', eqDialogEscapeHtmlAttr(params.close_button_text || ''));
            dialog_content += close_btn_html;
        }
        // Optional visible dialog heading (e.g. so a PDF/iframe dialog shows a title).
        // When present it becomes the dialog's accessible name (aria-labelledby) instead of the generic aria-label.
        if (params.dialog_title) {
            let title_id = params.dialog_id + '-title';
            jq(dialog).attr('aria-labelledby', title_id);
            jq(dialog).removeAttr('aria-label');
            dialog_content += `<h2 id="${eqDialogEscapeHtmlAttr(title_id)}" class="eq-dialog-title">${eqDialogEscapeHtmlAttr(params.dialog_title)}</h2>`;
        }
        if (params.dialog_type === 'inline') {
            let inline_div = document.getElementById(params.dialog_id);
            if (!inline_div) {
                console.error('Unable to find element with ID of \'#' + params.dialog_id + '\'');
                return false;
            }
            // Prefer an explicit source label, then the visible heading (e.g. announcement title),
            // so the <dialog> accessible name is not the generic default "Dialog".
            if (!params.dialog_title) {
                let has_custom_label = params.aria_label && params.aria_label !== settings.aria_label;
                if (!has_custom_label) {
                    let heading = inline_div.querySelector('h1, h2, h3');
                    let label = inline_div.dataset.arialabel || (heading && heading.textContent.trim()) || '';
                    if (label) {
                        jq(dialog).attr('aria-label', label);
                    }
                }
            }
            has_form = inline_div.querySelector('form');
            dialog_content += inline_div.innerHTML;
            
            // Hide target inline div (keep original IDs intact for reuse)
            if (this.settings.remove_inline_content) {
                inline_div.remove();
            } else {
                inline_div.style.display = 'none';
            }
        } else if (params.dialog_type === 'image') {
            if (!params.src) {
                console.error('EqDialog: image dialogs require a source URL.');
                return false;
            }
            dialog_content += `<img src="${eqDialogEscapeHtmlAttr(params.src)}" alt="${eqDialogEscapeHtmlAttr(params.img_alt || '')}">`;
        } else if (params.dialog_type === 'video') {
            if (!params.src) {
                console.error('EqDialog: video dialogs require a source URL.');
                return false;
            }
            // Build Video.js-compatible markup so dialog videos use the same player setup as the
            // item-video snippet (CT-454). It is initialized below via window.eqInitVideos(dialog).
            // Strip query/hash so the extension is not polluted (e.g. mp4?token=... -> mp4)
            let srcPathForExt = params.src.split(/[?#]/)[0];
            let matcher = /(?:\.([^.]+))?$/;
            let ext = ((matcher.exec(srcPathForExt) || [])[1] || '').toLowerCase();
            // .mov is mapped to video/mp4 because the QuickTime container type is unreliable across
            // browsers and .mov files are typically H.264/AAC (Video.js treats them as mp4).
            let mime_by_ext = { mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/mp4', webm: 'video/webm', ogg: 'video/ogg', ogv: 'video/ogg' };
            let format = mime_by_ext[ext] || 'video/mp4';
            let videoId = 'eq-dialog-video-' + params.dialog_id;
            let safeSrc = eqDialogEscapeHtmlAttr(params.src);
            let videoTitle = params.video_title || 'Video Player';
            let titleAttr = eqDialogEscapeHtmlAttr(videoTitle);
            dialog_content += `<div class="eq-video-wrapper">` +
                `<video id="${videoId}" class="video-js vjs-fluid" controls preload="auto" crossorigin="anonymous" ` +
                `data-standard-source="${safeSrc}" data-video-title="${titleAttr}" aria-label="${titleAttr}">` +
                `<source src="${safeSrc}" type="${eqDialogEscapeHtmlAttr(format)}">` +
                `</video></div>`;
            let transcript_link = eqDialogMediaDocLink(params.transcript_url, 'Video Transcript', videoTitle);
            let disclosure_link = eqDialogMediaDocLink(params.disclosure_url, 'Disclosure Statement', videoTitle);
            if (transcript_link || disclosure_link) {
                dialog_content += '<div class="inline-btn media-documents">' + transcript_link + disclosure_link + '</div>';
            }
        } else if (params.dialog_type === 'iframe') {
            let safe_iframe_src = eqDialogSafeExternalHref(params.src);
            if (!safe_iframe_src) {
                console.error('EqDialog: iframe dialogs require a safe http(s) source URL.');
                return false;
            }
            let labelAttr = params.description ? ` aria-label="${eqDialogEscapeHtmlAttr(params.description)}"` : '';
            dialog_content += `<iframe${labelAttr} src="${eqDialogEscapeHtmlAttr(safe_iframe_src)}"></iframe>`;
        } else {
            console.error(`EqDialog: unknown dialog type "${params.dialog_type}". Use inline, image, video, or iframe.`);
            return false;
        }

        // Apply styling
        let inner_el = dialog.querySelector('.eq-dialog-inner');
        for (const style_name in settings.style) {
            inner_el.style[style_name] = settings.style[style_name];
        }
        // Add content to dialog and add it to the DOM
        inner_el.innerHTML = dialog_content;
        document.body.appendChild(dialog);

        // Initialize Video.js for any video content within the dialog using the shared site setup
        // (same single source of truth as the item-video snippet / 99-custom.js).
        if (dialog.querySelector('.video-js')) {
            if (typeof window.eqInitVideos === 'function') {
                window.eqInitVideos(dialog);
            } else {
                console.warn("EqDialog: window.eqInitVideos is not available. Video dialogs require the Video.js bundle to be loaded on this page (set $page->value('videojs', true)).");
            }
        }

        // Popup specific functionality
        if (has_form) {
            hCaptchaOnLoad();
            validate_forms();
        }

        // Add event listener to hide the dialog when it is closed
        dialog.addEventListener('close', () => {
            // Dispose any Video.js players in the dialog to free resources and stop playback
            if (typeof videojs === 'function') {
                dialog.querySelectorAll('.video-js').forEach((video_el) => {
                    let player = videojs.getPlayer ? videojs.getPlayer(video_el) : null;
                    if (player) {
                        try {
                            player.dispose();
                        } catch (err) {
                            // ignore if already disposed
                        }
                    }
                });
            }
            dialog.remove();
        });

        // Close dialog when link inside dialog is clicked (use close() so the close handler runs and
        // Video.js players are disposed)
        jq(dialog)
            .find("a")
            .click(function () {
                dialog.close();
            });

        return dialog;
    }

    generate_dialog_id(dialog_type) {
        let index = 0;
        let id = dialog_type + '-dialog-' + index;
        while (document.getElementById(id)) {
            index++;
            id = dialog_type + '-dialog-' + index;
        }
        return id;
    }
}
